import React, { useRef, useEffect, useState, useCallback } from 'react';

export default function RadarChart({ data }) {
  const canvasRef = useRef(null);
  const [progress, setProgress] = useState(0);
  const [gridProgress, setGridProgress] = useState(0);
  const animationRef = useRef(null);
  const pulseRef = useRef(0);

  useEffect(() => {
    setProgress(0);
    setGridProgress(0);
    
    // 先让网格渐入
    const gridStartTime = Date.now();
    const gridDuration = 800;
    
    const animateGrid = () => {
      const elapsed = Date.now() - gridStartTime;
      const newProgress = Math.min(elapsed / gridDuration, 1);
      setGridProgress(newProgress);
      if (newProgress < 1) {
        requestAnimationFrame(animateGrid);
      }
    };
    
    requestAnimationFrame(animateGrid);

    // 延迟后开始数据动画
    const startTime = Date.now();
    const duration = 1500;

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const newProgress = Math.min(elapsed / duration, 1);
      const eased = newProgress === 1 ? 1 : 1 - Math.pow(2, -10 * newProgress);
      setProgress(eased);
      pulseRef.current = Date.now();
      if (newProgress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      }
    };

    const timer = setTimeout(() => {
      animationRef.current = requestAnimationFrame(animate);
    }, 600);

    return () => {
      clearTimeout(timer);
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [data]);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !data || data.length === 0) return;

    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx.scale(dpr, dpr);

    const width = rect.width;
    const height = rect.height;
    const centerX = width / 2;
    const centerY = height / 2;
    const radius = Math.min(width, height) / 2 - 40;
    const sides = data.length;
    const angleStep = (Math.PI * 2) / sides;
    const maxScore = Math.max(...data.map(d => d.score), 10);

    ctx.clearRect(0, 0, width, height);

    // 绘制背景网格（带渐入效果）
    for (let level = 1; level <= 4; level++) {
      const levelDelay = (level - 1) * 0.15;
      const levelProgress = Math.max(0, Math.min(1, (gridProgress - levelDelay) / 0.4));
      const r = (radius * level) / 4 * levelProgress;
      
      ctx.beginPath();
      for (let i = 0; i <= sides; i++) {
        const angle = i * angleStep - Math.PI / 2;
        const x = centerX + r * Math.cos(angle);
        const y = centerY + r * Math.sin(angle);
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.closePath();
      ctx.strokeStyle = `rgba(150, 130, 200, ${(0.1 + level * 0.03) * levelProgress})`;
      ctx.lineWidth = 1;
      ctx.stroke();
    }

    // 绘制轴线（带渐入效果）
    for (let i = 0; i < sides; i++) {
      const lineDelay = i * 0.05;
      const lineProgress = Math.max(0, Math.min(1, (gridProgress - lineDelay) / 0.5));
      const angle = i * angleStep - Math.PI / 2;
      const lineLength = radius * lineProgress;
      
      ctx.beginPath();
      ctx.moveTo(centerX, centerY);
      ctx.lineTo(centerX + lineLength * Math.cos(angle), centerY + lineLength * Math.sin(angle));
      ctx.strokeStyle = `rgba(150, 130, 200, ${0.15 * lineProgress})`;
      ctx.stroke();
    }

    // 绘制数据区域（带动画）
    if (progress > 0) {
      const points = data.map((d, i) => {
        const angle = i * angleStep - Math.PI / 2;
        const value = (d.score / maxScore) * radius * progress;
        return {
          x: centerX + value * Math.cos(angle),
          y: centerY + value * Math.sin(angle),
          color: d.color,
        };
      });

      // 填充区域
      ctx.beginPath();
      points.forEach((p, i) => {
        if (i === 0) ctx.moveTo(p.x, p.y);
        else ctx.lineTo(p.x, p.y);
      });
      ctx.closePath();
      const gradient = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, radius);
      gradient.addColorStop(0, `rgba(107, 91, 149, ${0.5 * progress})`);
      gradient.addColorStop(1, `rgba(107, 91, 149, ${0.15 * progress})`);
      ctx.fillStyle = gradient;
      ctx.fill();

      // 绘制边框线
      ctx.beginPath();
      points.forEach((p, i) => {
        if (i === 0) ctx.moveTo(p.x, p.y);
        else ctx.lineTo(p.x, p.y);
      });
      ctx.closePath();
      ctx.strokeStyle = `rgba(180, 160, 220, ${0.9 * progress})`;
      ctx.lineWidth = 2;
      ctx.stroke();

      // 绘制数据点（带发光效果）
      points.forEach((p) => {
        // 外发光
        const glowGradient = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, 12 * progress);
        glowGradient.addColorStop(0, `rgba(180, 160, 220, ${0.4 * progress})`);
        glowGradient.addColorStop(1, 'rgba(180, 160, 220, 0)');
        ctx.beginPath();
        ctx.arc(p.x, p.y, 12 * progress, 0, Math.PI * 2);
        ctx.fillStyle = glowGradient;
        ctx.fill();
        
        // 实心点
        ctx.beginPath();
        ctx.arc(p.x, p.y, 4 * progress, 0, Math.PI * 2);
        ctx.fillStyle = '#fff';
        ctx.fill();
      });
    }

    // 绘制标签
    data.forEach((d, i) => {
      const labelDelay = 0.3 + i * 0.03;
      const labelProgress = Math.max(0, Math.min(1, (gridProgress - labelDelay) / 0.4));
      const angle = i * angleStep - Math.PI / 2;
      const labelRadius = radius + 25;
      const x = centerX + labelRadius * Math.cos(angle);
      const y = centerY + labelRadius * Math.sin(angle);
      ctx.font = '11px "Noto Serif SC", serif';
      ctx.fillStyle = `rgba(160, 150, 180, ${labelProgress})`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(d.name, x, y);
    });
  }, [data, progress, gridProgress]);

  useEffect(() => {
    draw();
  }, [draw]);

  useEffect(() => {
    const handleResize = () => draw();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [draw]);

  return (
    <canvas
      ref={canvasRef}
      style={{ width: '100%', height: '100%', display: 'block' }}
    />
  );
}
